import React from 'react';
import { FiCheck, FiAlertCircle, FiX } from 'react-icons/fi';
import { formatBytes } from '../api/storageBoxApi';

function phaseLabel(phase) {
  if (phase === 'encrypting') return 'Encrypting';
  if (phase === 'uploading') return 'Uploading';
  if (phase === 'complete') return 'Upload complete';
  if (phase === 'error') return 'Upload failed';
  return 'Preparing';
}

/**
 * Non-modal upload status dock (percentage + file stats). Fixed to viewport bottom.
 */
export default function StorageBoxUploadProgressBar({ job, onDismiss }) {
  if (!job) return null;

  const showBar = job.phase === 'encrypting' || job.phase === 'uploading' || job.phase === 'preparing';
  const isDone = job.phase === 'complete';
  const isError = job.phase === 'error';
  if (!showBar && !isDone && !isError) return null;

  const percent = Math.max(0, Math.min(100, Math.round(job.percent || 0)));
  const totalBytes = job.totalBytes || 0;
  const loadedBytes = job.loadedBytes || 0;
  const fileLine =
    job.fileCount > 1
      ? `File ${Math.min(job.fileIndex || 1, job.fileCount)} of ${job.fileCount}${
          job.currentName ? ` · ${job.currentName}` : ''
        }`
      : job.currentName || 'Upload';

  const statsLine =
    totalBytes > 0
      ? `${formatBytes(loadedBytes)} of ${formatBytes(totalBytes)} · ${percent}%`
      : `${percent}%`;

  const rootClass = [
    'sb-upload-progress',
    isDone ? 'sb-upload-progress--complete' : '',
    isError ? 'sb-upload-progress--error' : '',
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <div className={rootClass} role="status" aria-live="polite" aria-label={phaseLabel(job.phase)}>
      <div className="sb-upload-progress__head">
        <div className="sb-upload-progress__titles">
          <span className="sb-upload-progress__phase">
            {isDone ? (
              <>
                <FiCheck aria-hidden /> {phaseLabel(job.phase)}
              </>
            ) : isError ? (
              <>
                <FiAlertCircle aria-hidden /> {phaseLabel(job.phase)}
              </>
            ) : (
              phaseLabel(job.phase)
            )}
          </span>
          <span className="sb-upload-progress__file" title={job.currentName || ''}>
            {fileLine}
          </span>
        </div>
        {(isDone || isError) && onDismiss ? (
          <button type="button" className="sb-upload-progress__close" onClick={onDismiss} aria-label="Dismiss">
            <FiX aria-hidden />
          </button>
        ) : null}
      </div>

      {showBar ? (
        <div className="sb-upload-progress__bar" aria-hidden>
          <div className="sb-upload-progress__bar-fill" style={{ width: `${percent}%` }} />
        </div>
      ) : null}

      <p className="sb-upload-progress__stats">{isError ? job.errorMessage || 'Upload failed' : statsLine}</p>
    </div>
  );
}
